import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, LogIn, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);

  // Where to go after login (default: homepage, or wherever user was trying to go)
  const redirectTo = location.state?.from || "/";

  // Surface the "session expired" reason after an auto-logout redirect
  useEffect(() => {
    if (sessionStorage.getItem("session_expired")) {
      sessionStorage.removeItem("session_expired");
      toast.error("Your session expired. Please sign in again to continue.");
    }
  }, []);

  // Redirect if already authenticated — role-aware routing
  useEffect(() => {
    if (!isAuthenticated) return;
    // Admin users with permissions should land on /admin, not /my-plans
    const adminRole = localStorage.getItem("admin_role");
    const adminPermsStr = localStorage.getItem("admin_permissions");
    if (adminRole && adminRole !== "user") {
      try {
        const adminPerms = JSON.parse(adminPermsStr || "[]");
        if (adminPerms.length > 0) {
          navigate("/admin", { replace: true });
          return;
        }
      } catch { /* ignore */ }
    }
    navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  const handleLevelUpLogin = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/levelup-login-url`);
      const loginUrl = res.data.login_url;
      const callbackUrl = `${window.location.origin}/authenticate-user`;
      // Persist the intended destination across the external LevelUp SSO redirect
      if (redirectTo && redirectTo !== "/my-plans") {
        localStorage.setItem("levelup_post_login_redirect", redirectTo);
      }
      window.location.href = `${loginUrl}?redirect_back=${encodeURIComponent(callbackUrl)}`;
    } catch {
      toast.error("Could not connect to LevelUp. Please try again.");
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#194449] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Verifying your identity...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-3 mb-6"
            data-testid="logo-home-link-login"
          >
            <img src="/technofocus-logo-white.png" alt="Technofocus" className="h-7 brightness-0 invert" style={{ filter: "none" }} />
            <div className="w-px h-6 bg-[#194449]/20" />
            <span className="text-xl font-semibold text-[#091F2C] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              MyGoals
            </span>
          </button>
          <h1
            className="text-2xl font-semibold text-[#091F2C] tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Sign in to MyGoals
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Access your personalized learning plans</p>
        </div>

        {/* Login Card — LevelUp SSO only */}
        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-8">
          <div className="space-y-4">
            <Button
              onClick={handleLevelUpLogin}
              disabled={loading}
              className="w-full bg-[#194449] text-white hover:bg-[#0d2f33] rounded-full h-12 font-medium shadow-md hover:shadow-lg transition-all duration-300"
              data-testid="levelup-login-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign in with LevelUp
                  <ExternalLink className="w-3 h-3 ml-2 opacity-60" />
                </>
              )}
            </Button>
          </div>
        </div>

        <p className="text-center text-xs text-[#94A3B8] mt-6">
          Powered by LevelUp by Technofocus
        </p>
      </div>
    </div>
  );
}
